import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { applyRefreshedAuthTokens, logoutFromApi } from '@/composables/authBridge'
import { redirectToLoginAfterSessionExpiry } from '@/router/routerBridge'
import type { ErrorResponse, RefreshTokenResponse } from '@/types/api'

const TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const TOKEN_EXPIRES_AT_KEY = 'token_expires_at'
const SESSION_EXPIRED_FLAG = '__sessionExpired'

const REFRESH_MARGIN_SECONDS = 60

let redirectingToLogin = false
let isRefreshing = false
let refreshTimer: ReturnType<typeof setTimeout> | null = null
let refreshQueue: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

export function apiBaseUrl(): string {
  const base = import.meta.env.VITE_APPLICATION_PATH || '/'
  return `${base.endsWith('/') ? base : `${base}/`}api`
}

/** Static assets under API public/ (recorder.js, recorder.version, …). */
export function apiPublicAssetUrl(filename: string): string {
  const base = apiBaseUrl().replace(/\/$/, '')
  const name = filename.replace(/^\//, '')
  return `${base}/${name}`
}

export function absoluteUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path
  }
  const normalized = path.startsWith('/') ? path : `/${path}`
  if (typeof window === 'undefined') {
    return normalized
  }
  return `${window.location.origin}${normalized}`
}

export const api = axios.create({
  baseURL: apiBaseUrl(),
  headers: {
    Accept: 'application/json',
  },
})

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function getTokenExpiresAt(): number | null {
  const raw = localStorage.getItem(TOKEN_EXPIRES_AT_KEY)
  if (raw === null || raw === '') {
    return null
  }

  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

export function setAuthTokens(accessToken: string, refreshToken: string, expiresIn: number): void {
  localStorage.setItem(TOKEN_KEY, accessToken)
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  localStorage.setItem(TOKEN_EXPIRES_AT_KEY, String(Date.now() + expiresIn * 1000))
  scheduleTokenRefresh(expiresIn)
}

export function setStoredToken(token: string | null): void {
  if (token === null) {
    clearAuthTokens()
    return
  }

  localStorage.setItem(TOKEN_KEY, token)
}

export function clearAuthTokens(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY)
  clearTokenRefresh()
}

function clearTokenRefresh(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

export function scheduleTokenRefresh(expiresIn: number): void {
  clearTokenRefresh()

  const refreshInSeconds = Math.max(expiresIn - REFRESH_MARGIN_SECONDS, Math.floor(expiresIn * 0.8), 5)
  refreshTimer = setTimeout(() => {
    void performTokenRefresh()
  }, refreshInSeconds * 1000)
}

export async function performTokenRefresh(): Promise<RefreshTokenResponse> {
  const tokens = await requestTokenRefresh()
  await applyRefreshedTokens(tokens)
  return tokens
}

async function requestTokenRefresh(): Promise<RefreshTokenResponse> {
  const refreshToken = getStoredRefreshToken()
  if (!refreshToken) {
    throw new Error('Refresh token ausente')
  }

  const { data } = await axios.post<RefreshTokenResponse>(
    `${apiBaseUrl()}/refresh`,
    { refresh_token: refreshToken },
    {
      headers: {
        Accept: 'application/json',
      },
    },
  )

  return data
}

async function applyRefreshedTokens(tokens: RefreshTokenResponse): Promise<void> {
  applyRefreshedAuthTokens(tokens)
}

function isLoginRequest(config: InternalAxiosRequestConfig | undefined): boolean {
  const url = config?.url ?? ''
  const method = config?.method?.toLowerCase() ?? ''
  return url.includes('/login') && method === 'post'
}

function isRefreshRequest(config: InternalAxiosRequestConfig | undefined): boolean {
  const url = config?.url ?? ''
  const method = config?.method?.toLowerCase() ?? ''
  return url.includes('/refresh') && method === 'post'
}

async function handleSessionExpired(): Promise<void> {
  if (redirectingToLogin) {
    return
  }

  redirectingToLogin = true

  await logoutFromApi()
  await redirectToLoginAfterSessionExpiry()
}

function queueTokenRefresh(): Promise<string> {
  return new Promise((resolve, reject) => {
    refreshQueue.push({ resolve, reject })
  })
}

function resolveRefreshQueue(token: string): void {
  for (const pending of refreshQueue) {
    pending.resolve(token)
  }
  refreshQueue = []
}

function rejectRefreshQueue(error: unknown): void {
  for (const pending of refreshQueue) {
    pending.reject(error)
  }
  refreshQueue = []
}

function retryRequest(config: InternalAxiosRequestConfig, token: string) {
  config.params = {
    ...config.params,
    access_token: token,
  }

  return api.request(config)
}

async function tryRefreshAndRetry(error: AxiosError<ErrorResponse | string>): Promise<unknown> {
  const originalConfig = error.config
  if (!originalConfig) {
    return Promise.reject(error)
  }

  if (isRefreshing) {
    const token = await queueTokenRefresh()
    return retryRequest(originalConfig, token)
  }

  isRefreshing = true

  try {
    const tokens = await performTokenRefresh()
    resolveRefreshQueue(tokens.access_token)
    return retryRequest(originalConfig, tokens.access_token)
  } catch (refreshError) {
    rejectRefreshQueue(refreshError)
    void handleSessionExpired()
    Object.assign(error, { [SESSION_EXPIRED_FLAG]: true })
    return new Promise(() => {})
  } finally {
    isRefreshing = false
  }
}

api.interceptors.request.use((config) => {
  const token = getStoredToken()
  if (token) {
    config.params = {
      ...config.params,
      access_token: token,
    }
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ErrorResponse | string>) => {
    const status = error.response?.status

    if (status === 401 && !isLoginRequest(error.config) && !isRefreshRequest(error.config)) {
      if (getStoredRefreshToken()) {
        return tryRefreshAndRetry(error)
      }

      void handleSessionExpired()
      Object.assign(error, { [SESSION_EXPIRED_FLAG]: true })
      return new Promise(() => {})
    }

    if (status !== undefined && status >= 400) {
      // errors surfaced by callers
    }

    return Promise.reject(error)
  },
)

export function isSessionExpiredError(error: unknown): boolean {
  return axios.isAxiosError(error)
    && (error as unknown as Record<string, unknown>)[SESSION_EXPIRED_FLAG] === true
}

export function getApiErrorMessage(error: unknown, fallback = 'Erro inesperado'): string {
  if (!axios.isAxiosError(error)) {
    return fallback
  }

  const axiosError = error as AxiosError<ErrorResponse | string>
  const data = axiosError.response?.data

  if (typeof data === 'object' && data !== null && 'error' in data && typeof data.error === 'string') {
    return data.error
  }

  if (typeof data === 'string' && data !== '') {
    return data
  }

  if (axiosError.response?.status === 401) {
    return 'Sessão expirada. Faça login novamente.'
  }

  return fallback
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || !getStoredRefreshToken()) {
      return
    }

    const expiresAt = getTokenExpiresAt()
    if (expiresAt !== null && expiresAt - Date.now() < REFRESH_MARGIN_SECONDS * 1000) {
      void performTokenRefresh().catch(() => {
        // O interceptor trata falhas em chamadas autenticadas.
      })
    }
  })
}

const initialExpiresAt = getTokenExpiresAt()
if (initialExpiresAt !== null && getStoredRefreshToken()) {
  const secondsLeft = Math.floor((initialExpiresAt - Date.now()) / 1000)
  if (secondsLeft > 0) {
    scheduleTokenRefresh(secondsLeft)
  } else {
    void performTokenRefresh().catch(() => {
      // Falha silenciosa no boot; initialize() tentará novamente.
    })
  }
}
